import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, MapPin, Clock, X, Ticket, Users } from "lucide-react";
import RsvpModal from "./RsvpModal";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function EventCalendar({ events = [] }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(null);
  const [rsvpEvent, setRsvpEvent] = useState(null);

  const monthStart = cursor;
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const startDow = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();

  const byDate = useMemo(() => {
    const map = {};
    for (const e of events) {
      if (!e?.date) continue;
      (map[e.date] ??= []).push(e);
    }
    return map;
  }, [events]);

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(cursor.getFullYear(), cursor.getMonth(), d);
    cells.push({ date, key: ymd(date), day: d });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const goto = (delta) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));
  const goToday = () => { setCursor(new Date(today.getFullYear(), today.getMonth(), 1)); setSelected(ymd(today)); };

  const selectedEvents = selected ? (byDate[selected] || []) : [];

  return (
    <div className="grid gap-8 md:grid-cols-12" data-testid="event-calendar">
      <div className="md:col-span-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl md:text-3xl font-bold">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h3>
          <div className="flex items-center gap-1">
            <button onClick={() => goto(-1)} data-testid="cal-prev" aria-label="Previous month" className="h-9 w-9 rounded-md border border-black/15 hover:bg-black/5 flex items-center justify-center"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={goToday} data-testid="cal-today" className="h-9 px-3 rounded-md border border-black/15 hover:bg-black/5 text-sm">Today</button>
            <button onClick={() => goto(1)} data-testid="cal-next" aria-label="Next month" className="h-9 w-9 rounded-md border border-black/15 hover:bg-black/5 flex items-center justify-center"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="grid grid-cols-7 border-l border-t border-black/10 bg-white">
          {DOW.map((d) => (
            <div key={d} className="border-r border-b border-black/10 px-2 py-2 text-[10px] uppercase tracking-widest text-black/50 font-semibold">{d}</div>
          ))}
          {cells.map((c, i) => {
            if (!c) return <div key={i} className="border-r border-b border-black/10 bg-black/[0.02] min-h-[92px]" />;
            const isToday = c.key === ymd(today);
            const isSel = c.key === selected;
            const dayEvents = byDate[c.key] || [];
            return (
              <button
                key={i}
                onClick={() => setSelected(isSel ? null : c.key)}
                data-testid={`cal-day-${c.key}`}
                className={`border-r border-b border-black/10 min-h-[92px] p-2 text-left align-top transition-colors ${
                  isSel ? "bg-[#6B21A8] text-white" : isToday ? "bg-[#F5B90A]/15" : "bg-white hover:bg-[#6B21A8]/5"
                }`}
              >
                <span className={`inline-flex h-6 w-6 items-center justify-center text-sm font-semibold ${
                  isSel ? "bg-white text-[#6B21A8] rounded-full" : isToday ? "text-[#6B21A8]" : "text-black"
                }`}>{c.day}</span>
                <div className="mt-1 space-y-0.5">
                  {dayEvents.slice(0, 2).map((e) => (
                    <p key={e.id} className={`truncate text-[11px] leading-tight px-1 rounded ${
                      isSel ? "bg-white/20 text-white" : "bg-[#6B21A8]/10 text-[#6B21A8]"
                    }`}>{e.title}</p>
                  ))}
                  {dayEvents.length > 2 && (
                    <p className={`text-[10px] ${isSel ? "text-white/80" : "text-black/60"}`}>+{dayEvents.length - 2} more</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <aside className="md:col-span-4">
        <div className="sticky top-24">
          <div className="flex items-center justify-between mb-4">
            <p className="overline text-[#6B21A8]">{selected ? new Date(selected + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) : "Pick a date"}</p>
            {selected && <button onClick={() => setSelected(null)} className="text-black/50 hover:text-black" aria-label="Clear"><X className="h-4 w-4" /></button>}
          </div>
          {selected ? (
            selectedEvents.length === 0 ? (
              <p className="text-sm text-black/60 border border-dashed border-black/15 p-4">Nothing scheduled this day.</p>
            ) : (
              <div className="space-y-3" data-testid="cal-selected-events">
                {selectedEvents.map((e) => (
                  <article key={e.id} className="border border-black/10 bg-white p-4" data-testid={`cal-event-${e.id}`}>
                    <p className="overline text-[#6B21A8]">{e.category || "Event"}</p>
                    <h4 className="mt-2 font-display text-lg font-semibold leading-tight">{e.title}</h4>
                    {e.time && <p className="mt-2 text-xs text-black/60 flex items-center gap-1"><Clock className="h-3 w-3" /> {e.time}</p>}
                    {e.location && <p className="text-xs text-black/60 flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.location}</p>}
                    {e.description && <p className="mt-3 text-sm text-black/70 line-clamp-3">{e.description}</p>}
                    {e.rsvp_enabled !== false && (
                      <button
                        onClick={() => setRsvpEvent(e)}
                        data-testid={`rsvp-open-${e.id}`}
                        className={`mt-4 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-transform hover:-translate-y-0.5 ${
                          e.ticket_price > 0 ? "bg-[#F5B90A] text-black" : "bg-[#6B21A8] text-white"
                        }`}
                      >
                        {e.ticket_price > 0 ? <><Ticket className="h-4 w-4" /> Get ticket · ${e.ticket_price?.toFixed(0)}</> : <><Users className="h-4 w-4" /> RSVP</>}
                      </button>
                    )}
                  </article>
                ))}
              </div>
            )
          ) : (
            <p className="text-sm text-black/60 border border-dashed border-black/15 p-4">Click any highlighted day to see the events scheduled.</p>
          )}
        </div>
      </aside>
      {rsvpEvent && <RsvpModal event={rsvpEvent} onClose={() => setRsvpEvent(null)} />}
    </div>
  );
}
